import { useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";

const FORM_URL = "https://readdy.ai/api/form/db7466raskivmpnrcfl0";

const footerLinks = [
  { to: "/", label: "首页" },
  { to: "/categories", label: "分类列表" },
  { to: "/specials", label: "潮流专题" },
  { to: "/search", label: "全站搜索" },
  { to: "/about", label: "关于我们" },
];

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);
    const honeypot = String(payload.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，感谢关注黑丝潮流资讯。");
      form.reset();
      return;
    }

    payload.delete("website_alt");
    setStatus("loading");
    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，感谢关注黑丝潮流资讯。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  };

  const hotTags = articles.slice(0, 6).flatMap((item) => item.tags).slice(0, 10);
  const uniqueTags = Array.from(new Set(hotTags));

  return (
    <footer className="mt-16 bg-primary-950 text-background-100">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 font-heading text-lg font-bold text-primary-950">
                黑
              </span>
              <span className="font-heading text-2xl font-bold tracking-wide">黑丝</span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-background-200/90">
              黑丝 · 时尚搭配与潮流资讯站。我们专注于黑丝丝袜的搭配技巧、实用穿搭与流行趋势，为追求个性美感的你提供持续的灵感。
            </p>
            <div className="mt-5 flex items-center gap-3">
              {[
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-reddit-line", label: "社区" },
                { icon: "ri-mail-line", label: "邮箱" },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  aria-label={item.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/10 text-lg text-background-100 transition-colors hover:bg-background-50/20 cursor-pointer"
                >
                  <i className={item.icon} />
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-heading text-base font-semibold text-background-50">
              <Link to="/categories">内容导航</Link>
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              {footerLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-background-200/85 transition-colors hover:text-background-50">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-heading text-base font-semibold text-background-50">
              <Link to="/categories">热门标签</Link>
            </h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {uniqueTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-50/10 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-background-50/20"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading text-base font-semibold text-background-50">
              <Link to="/about">订阅潮流资讯</Link>
            </h4>
            <p className="mt-3 text-sm text-background-200/85">每周精选搭配灵感与趋势解读，送达你的邮箱。</p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
              <input
                type="email"
                name="email"
                required
                placeholder="你的邮箱地址"
                className="h-11 w-full rounded-lg border border-background-50/20 bg-background-50/10 px-4 text-sm text-background-50 outline-none placeholder:text-background-200/60 focus:border-primary-300"
              />
              <input
                className="hp-field-1993"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="h-11 w-full whitespace-nowrap rounded-lg bg-accent-500 px-4 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:opacity-60 cursor-pointer"
              >
                {status === "loading" ? "提交中…" : "立即订阅"}
              </button>
            </form>
            {status !== "idle" && status !== "loading" && (
              <p className={`mt-2 text-xs ${status === "success" ? "text-accent-300" : "text-primary-300"}`}>
                {message}
              </p>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-background-50/15 pt-6 text-xs text-background-200/70 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2024 黑丝 · 时尚搭配与潮流资讯站. 保留所有权利。</p>
          <p>本站内容为时尚搭配资讯分享，版权归原作者所有。</p>
        </div>
      </div>
    </footer>
  );
}